import { useEffect, useRef, useState } from 'react';
import { ALL_ITEM_TYPES, type ItemType, type LayerDef } from '../../doc/schema';
import { IconDots, IconEye, IconEyeOff, IconLock, IconUnlock } from '../../ui/icons';
import { PanelFrame } from '../floating';
import { useBoard, useItemsVersion, usePaneStore, useYMap } from '../context';
import { setToolSettings } from '../toolSettings';

const TYPE_LABEL: Record<ItemType, string> = {
  frame: 'frame',
  image: 'img',
  note: 'note',
  title: 'title',
  richtext: 'text',
  table: 'table',
  todo: 'todo',
  kanban: 'kanban',
  shape: 'shape',
  stroke: 'ink',
  link: 'link',
  file: 'file',
};

/** Types a layer can auto-collect. Strokes are not offered: they always sit on their tool's ink layer. */
const CHIP_TYPES = ALL_ITEM_TYPES.filter((t) => t !== 'stroke');

/**
 * Layers panel. Rows are top-to-bottom in stacking order; "Default" is the implicit bottom layer
 * for items without a layer. Each layer can auto-collect item types on creation; "apply" moves
 * existing items of those types too. Ink layers (one per pen tool) appear once drawn on; they
 * hold strokes only, and selecting their items switches to the lasso.
 */
export function LayersPanel({ onClose }: { onClose: () => void }) {
  const board = useBoard();
  const pane = usePaneStore();
  const layers = useYMap<LayerDef>(board.layers);
  useItemsVersion();
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuFor) return;
    const close = (e: PointerEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuFor(null);
    };
    window.addEventListener('pointerdown', close, true);
    return () => window.removeEventListener('pointerdown', close, true);
  }, [menuFor]);

  const rows = Object.entries(layers).sort((a, b) => b[1].order - a[1].order); // top first
  const counts = new Map<string | null, number>();
  for (const item of board.index.all()) {
    const key = item.layerId && layers[item.layerId] ? item.layerId : null;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const add = () => {
    const name = prompt('Layer name', `Layer ${rows.length + 1}`);
    if (name?.trim()) board.createLayer(name.trim());
  };

  const act = (fn: () => void) => () => {
    setMenuFor(null);
    fn();
  };

  const selectOn = (layerId: string | null) => {
    const ink = layerId ? !!layers[layerId]?.ink : false;
    const ids = board.index
      .all()
      .filter((i) => (i.layerId && layers[i.layerId] ? i.layerId : null) === layerId && !board.isHidden(i) && (i.type === 'stroke') === ink)
      .map((i) => i.id);
    const s = pane.getState();
    if (ink) {
      // strokes are only selectable with the draw tool: hand over to the lasso
      setToolSettings({ penMode: 'lasso' });
      s.setTool('draw');
    } else if (s.tool === 'draw') {
      s.setTool('select');
    }
    s.select(ids);
  };

  const row = (id: string | null, def: LayerDef | null, idx: number) => {
    const name = def?.name ?? 'Default';
    const visible = def?.visible ?? true;
    const locked = def?.locked ?? false;
    const auto = def?.autoTypes ?? [];
    const ink = def?.ink ?? null;
    const count = counts.get(id) ?? 0;
    return (
      <div key={id ?? '__default'} className={`layer-row${visible ? '' : ' is-hidden'}${locked ? ' is-locked' : ''}`}>
        <div className="layer-main">
          <button
            type="button"
            className="layer-toggle"
            title={visible ? 'Hide layer' : 'Show layer'}
            disabled={!def}
            onClick={() => def && board.updateLayer(id!, { visible: !visible })}
          >
            {visible ? <IconEye /> : <IconEyeOff />}
          </button>
          <button
            type="button"
            className="layer-toggle"
            title={locked ? 'Unlock layer' : 'Lock layer'}
            disabled={!def}
            onClick={() => def && board.updateLayer(id!, { locked: !locked })}
          >
            {locked ? <IconLock /> : <IconUnlock />}
          </button>
          <button
            type="button"
            className="layer-name"
            title="Click to select the layer's items; double-click to rename"
            onClick={() => selectOn(id)}
            onDoubleClick={() => {
              if (!def) return;
              const n = prompt('Layer name', name);
              if (n?.trim()) board.updateLayer(id!, { name: n.trim() });
            }}
          >
            {name}
          </button>
          {ink && (
            <span className="layer-badge" title={`Ink layer: every ${ink} stroke lands here`}>
              ink
            </span>
          )}
          <span className="layer-count">{count}</span>
          {def && (
            <span className="layer-menu-host">
              <button type="button" className="layer-toggle" aria-label={`Options for ${name}`} onClick={() => setMenuFor(menuFor === id ? null : id)}>
                <IconDots />
              </button>
              {menuFor === id && (
                <div className="menu layer-menu" ref={menuRef} role="menu">
                  <button type="button" role="menuitem" disabled={idx === 0} onClick={act(() => board.moveLayer(id!, +1))}>
                    <span>Move up</span>
                  </button>
                  <button type="button" role="menuitem" disabled={idx === rows.length - 1} onClick={act(() => board.moveLayer(id!, -1))}>
                    <span>Move down</span>
                  </button>
                  {!ink && (
                    <>
                      <button type="button" role="menuitem" onClick={act(() => board.moveSelectionToLayer(id, [...pane.getState().selection]))}>
                        <span>Move selection here</span>
                      </button>
                      <button type="button" role="menuitem" disabled={!auto.length} onClick={act(() => board.applyAutoLayer(id!))}>
                        <span>Move all {auto.length ? auto.map((t) => TYPE_LABEL[t]).join('/') : '…'} items here</span>
                      </button>
                    </>
                  )}
                  <div className="sep" />
                  <button
                    type="button"
                    role="menuitem"
                    className="danger"
                    disabled={!!ink && count > 0}
                    title={ink && count > 0 ? 'Erase its strokes first' : undefined}
                    onClick={act(() => {
                      if (confirm(`Delete layer "${name}"? Its items move to Default.`)) board.deleteLayer(id!);
                    })}
                  >
                    <span>Delete layer</span>
                  </button>
                </div>
              )}
            </span>
          )}
        </div>
        {def && !ink && (
          <div className="layer-types" title="New items of these types land on this layer">
            {CHIP_TYPES.map((t) => (
              <button
                key={t}
                type="button"
                className={`chip${auto.includes(t) ? ' is-active' : ''}`}
                onClick={() => board.toggleAutoType(id!, t)}
              >
                {TYPE_LABEL[t]}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <PanelFrame kind="layers" title="Layers" className="layers-panel" onClose={onClose}>
      <div className="layers-body">
        {rows.map(([id, def], i) => row(id, def, i))}
        {row(null, null, rows.length)}
      </div>
      <div className="layers-foot">
        <button type="button" className="btn" onClick={add}>
          + New layer
        </button>
      </div>
    </PanelFrame>
  );
}
